import React, { useState, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, inpS, sp, rad, icBox } from "../../constants/styles";
import { glassPanel } from "../../constants/gradCard";
import { rentPeriod } from "../../utils/dateFormat";
import Fld from "../common/Fld";
import Avatar from "../common/Avatar";
import ReceiptView from "./ReceiptView";
import { isOverdue } from "../../utils/leaseHelpers";
import { applyAdvanceToDue } from "../../utils/advance";
import { IS_DESKTOP } from "../../constants/platform";

const normPhone = (ph) => { const clean = (ph || "").replace(/\D/g, ""); return clean.length === 10 ? "91" + clean : clean; };

// Compute the rent period a payment covers from its month label (mo,
// e.g. "Jun 26" = collected in Jun 2026) and the tenant's rent_due_day.
//
// ARREARS convention: rent is paid for the month JUST FINISHED, so a
// payment labelled "Jun 26" covers the PREVIOUS month (May). The period
// therefore runs from the due day of (month - 1) to the day before the
// due day of (month):
//   "Jun 26", due day 1  → 1 May – 31 May 2026
//   "Jun 26", due day 5  → 5 May – 4 Jun 2026
// new Date() handles the month/year underflow for January automatically
// (e.g. "Jan 26" → 1 Dec 2025 – 31 Dec 2025).
//
// Display-only: expand a stored month label "Jun 26" → "Jun 2026" so the
// 2-digit year can't be misread as a day-of-month. The stored p.mo value
// is left untouched (payment matching / generation / dedup all key off
// the exact "Mon YY" string).
const moLabel = (mo) => {
  if (!mo || typeof mo !== "string") return mo || "";
  const parts = mo.trim().split(/\s+/);
  if (parts.length < 2) return mo;
  const yy = parseInt(parts[1], 10);
  if (Number.isNaN(yy)) return mo;
  const year = yy < 100 ? 2000 + yy : yy;
  return `${parts[0]} ${year}`;
};

function PayPage() {
  const { t, h, pays, setPays, tens, props, go, flash, addNotif, showConfirm, plan } = useContext(Ctx); const hasWa = (plan.features || []).includes("whatsapp"); const [tab, sT] = useState("all"); const [expTen, setExpTen] = useState({});
  // Tracks which combined "🔧 Common & Maint" cards are expanded (per-tenant key).
  const [expMaint, setExpMaint] = useState({});
  // Tracks which PAID payment cards the landlord has explicitly expanded.
  // Mirrors the maintenance "done card collapse" pattern: paid rows are
  // settled and don't need action, so collapse them by default to keep
  // the list scannable. Due / partial / pending rows stay always-expanded
  // because they need the action buttons (Mark Paid / Approve / etc.).
  const [expandedPaidIds, setExpandedPaidIds] = useState(new Set());
  const togglePaidExpanded = (id) => setExpandedPaidIds(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const [waAction, setWaAction] = useState(null);
  const pendingCount = pays.filter(p => p.pending).length;
  const paidList = pays.filter(p => p.st === "paid" && !p.pending);
  const dueList = pays.filter(p => (p.st === "due" || p.st === "partial") && !p.pending);
  const advanceList = pays.filter(p => p.st === "advance" || (p.advance && p.advance > 0));
  const paidCount = paidList.length;
  const dueCount = dueList.length;
  const advanceCount = advanceList.length;
  const paidTotal = paidList.reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const advanceTotal = pays.reduce((s, p) => s + (p.advance || 0), 0);
  const dueTotal = dueList.reduce((s, p) => {
    const gross = p.amt + (p.com || 0);
    const txnPaid = (p.txns || []).reduce((sum, tx) => sum + (tx.status === "verified" ? tx.amt : 0), 0);
    return s + Math.max(0, gross - txnPaid);
  }, 0);
  const filtered = tab === "all" ? pays : tab === "pending" ? pays.filter(p => p.pending) : tab === "due" ? pays.filter(p => (p.st === "due" || p.st === "partial") && !p.pending) : tab === "advance" ? advanceList : pays.filter(p => p.st === tab && !p.pending);
  // Sort: latest activity first (by latest txn date, then by payment date)
  const f = [...filtered].sort((a, b) => {
    const lastTxnA = (a.txns || []).reduce((max, tx) => tx.dt > max ? tx.dt : max, "");
    const lastTxnB = (b.txns || []).reduce((max, tx) => tx.dt > max ? tx.dt : max, "");
    const dateA = lastTxnA || a.dt || a.mo || "";
    const dateB = lastTxnB || b.dt || b.mo || "";
    return dateB.localeCompare(dateA);
  });
  const [editId, sEd] = useState(null); const [eAmt, sEA] = useState(""); const [eCom, sEC] = useState(""); const [eMth, sEM] = useState(""); const [eSt, sES] = useState("");
  // When the landlord clicks Mark Paid, we open a small inline method
  // picker on that payment row (Cash / UPI / Bank / Cheque). Replaces
  // the old behaviour where the method silently defaulted to "razorpay"
  // for partial payments and "cash" otherwise — which lied about what
  // actually happened. Stored value: payment id of the row whose picker
  // is currently open, or null when no picker is shown.
  const [methodPickerFor, setMethodPickerFor] = useState(null);
  // Receipt viewer for paid rows — same ReceiptView the tenant gets, so
  // the landlord can download/share the identical document.
  const [receiptPay, setReceiptPay] = useState(null);
  const RENT_PAYMENT_METHODS = [
    { id: "cash",   label: "Cash",          icon: "💵" },
    { id: "upi",    label: "UPI",           icon: "📱" },
    { id: "bank",   label: "Bank Transfer", icon: "🏦" },
    { id: "cheque", label: "Cheque",        icon: "📝" },
  ];
  const markPaidWithMethod = (p, method) => {
    const up = { ...p, st: "paid", mth: method, dt: new Date().toISOString().slice(0, 10) };
    setPays(pays.map(x => x.id === p.id ? up : x));
    api.savePayment(up);
    flash(t.markedPaid);
    setMethodPickerFor(null);
  };
  const startEdit = (p) => { sEd(p.id); sEA(p.amt.toString()); sEC((p.com || 0).toString()); sEM(p.mth || "cash"); sES(p.st); };
  const saveEdit = () => {
    const uPay = pays.find(x => x.id === editId);
    const updated = { ...uPay, amt: Number(eAmt), com: Number(eCom), mth: eMth, st: eSt, dt: eSt === "paid" && !uPay.dt ? new Date().toISOString().slice(0, 10) : uPay.dt };
    setPays(pays.map(x => x.id === editId ? updated : x));
    api.savePayment(updated).catch(() => flash("Save failed", "error"));
    flash(t.updated); sEd(null);
  };
  const deletePayment = (payId) => {
    showConfirm(t.deletePayConfirm, () => {
      setPays(pays.filter(x => x.id !== payId));
      api.deletePayment(payId).catch(() => flash("Delete failed", "error"));
      addNotif("landlord", null, "Deleted a payment record", "info");
      flash(t.deleted, "warn");
    });
  };

  const approvePayment = (payId) => {
    const pay = pays.find(x => x.id === payId); if (!pay || !pay.pending) return;
    const pAmt = pay.pending.amt; const totalDue2 = pay.amt + (pay.com || 0);
    const tName = tens.find(x => x.id === pay.tid)?.name || "Tenant";
    const approvedTxn = { id: `tx${Date.now()}`, tid: pay.tid, amt: pAmt, mth: pay.pending.mth, dt: pay.pending.dt, type: pAmt >= totalDue2 ? "full" : "partial", status: "approved", mo: pay.mo, approvedAt: new Date().toISOString().slice(0, 10), totalWas: totalDue2, remaining: Math.max(totalDue2 - pAmt, 0) };
    const updatedTxns = (pay.txns || []).map(tx => tx.status === "pending" ? { ...tx, status: "approved", approvedAt: new Date().toISOString().slice(0, 10), remaining: Math.max(totalDue2 - pAmt, 0) } : tx);
    if (pAmt >= totalDue2) {
      // Anything paid beyond the due (or a pure advance submission, where
      // totalDue2 is 0) becomes advance credit on the row — previously the
      // excess was silently dropped on approval and the tenant's credit
      // vanished.
      const overpay = Math.max(0, pAmt - totalDue2);
      const up = { ...pay, st: "paid", mth: pay.pending.mth, dt: pay.pending.dt, pending: undefined, txns: updatedTxns.length ? updatedTxns : [approvedTxn], advance: (pay.advance || 0) + overpay };
      setPays(pays.map(x => x.id === payId ? up : x));
      api.savePayment(up);
      addNotif("landlord", null, `Approved \u20B9${pAmt.toLocaleString()} payment from ${tName}`, "payment");
      addNotif("tenant", pay.tid, `Your full payment of \u20B9${pAmt.toLocaleString()} has been approved! \u2713`, "payment");
      flash(`\u2713 ${t.fullPayApproved} \u20B9${pAmt.toLocaleString()}`);
      const aTe = tens.find(x => x.id === pay.tid);
      if (aTe?.ph) setWaAction({ tid: pay.tid, name: aTe.name, ph: aTe.ph, msg: `\u2705 *${t.payApprovedWa}*\n\nHi ${aTe.name},\n\nYour payment of \u20B9${pAmt.toLocaleString()} for ${pay.mo} has been received and approved.\n\nThank you!` });
    } else {
      const newRent = pay.amt - Math.min(pAmt, pay.amt); const leftover = pAmt > pay.amt ? pAmt - pay.amt : 0; const newCom = Math.max((pay.com || 0) - leftover, 0);
      const up = { ...pay, amt: newRent, com: newCom, mth: pay.pending.mth, st: (newRent + newCom) <= 0 ? "paid" : "due", pending: undefined, txns: updatedTxns.length ? updatedTxns : [approvedTxn] };
      setPays(pays.map(x => x.id === payId ? up : x));
      api.savePayment(up);
      addNotif("landlord", null, `Approved partial \u20B9${pAmt.toLocaleString()} from ${tName}`, "payment");
      addNotif("tenant", pay.tid, `Partial payment \u20B9${pAmt.toLocaleString()} approved. Remaining: \u20B9${(totalDue2 - pAmt).toLocaleString()}`, "payment");
      flash(`\u2713 ${t.partialApproved} \u20B9${pAmt.toLocaleString()} ${t.remaining}: \u20B9${(totalDue2 - pAmt).toLocaleString()}`);
      const aTe2 = tens.find(x => x.id === pay.tid);
      if (aTe2?.ph) setWaAction({ tid: pay.tid, name: aTe2.name, ph: aTe2.ph, msg: `\u2705 *${t.payApprovedWa}*\n\nHi ${aTe2.name},\n\nYour partial payment of \u20B9${pAmt.toLocaleString()} for ${pay.mo} has been approved. Remaining: \u20B9${(totalDue2 - pAmt).toLocaleString()}.\n\nThank you!` });
    }
  };
  // One-shot approval for every pending row that shares a batchId.
  // Avoids the "approve 3 cards in a row" tedium when a tenant submitted
  // a single payment that got allocated across rent + maintenance rows.
  const approveBatch = (batchId) => {
    const batchPays = pays.filter(p => p.pending?.batchId === batchId);
    if (batchPays.length === 0) return;
    let totalApproved = 0;
    const updateMap = new Map();
    const todayStr = new Date().toISOString().slice(0, 10);
    for (const pay of batchPays) {
      const pAmt = pay.pending.amt;
      totalApproved += pAmt;
      const totalDue2 = pay.amt + (pay.com || 0);
      const updatedTxns = (pay.txns || []).map(tx => tx.status === "pending" ? { ...tx, status: "approved", approvedAt: todayStr, remaining: Math.max(totalDue2 - pAmt, 0) } : tx);
      if (pAmt >= totalDue2) {
        // Overpay → advance credit (same rule as single approval above).
        const overpay = Math.max(0, pAmt - totalDue2);
        updateMap.set(pay.id, { ...pay, st: "paid", mth: pay.pending.mth, dt: pay.pending.dt, pending: undefined, txns: updatedTxns, advance: (pay.advance || 0) + overpay });
      } else {
        const newRent = pay.amt - Math.min(pAmt, pay.amt);
        const leftover = pAmt > pay.amt ? pAmt - pay.amt : 0;
        const newCom = Math.max((pay.com || 0) - leftover, 0);
        updateMap.set(pay.id, { ...pay, amt: newRent, com: newCom, mth: pay.pending.mth, st: (newRent + newCom) <= 0 ? "paid" : "due", pending: undefined, txns: updatedTxns });
      }
    }
    setPays(pays.map(x => updateMap.get(x.id) || x));
    updateMap.forEach(u => api.savePayment(u));
    const tid0 = batchPays[0].tid;
    const tName = tens.find(x => x.id === tid0)?.name || "Tenant";
    addNotif("landlord", null, `Approved ₹${totalApproved.toLocaleString()} batch from ${tName} (${batchPays.length} charges)`, "payment");
    addNotif("tenant", tid0, `Your ₹${totalApproved.toLocaleString()} payment across ${batchPays.length} charges has been approved! ✓`, "payment");
    flash(`✓ Approved ₹${totalApproved.toLocaleString()} across ${batchPays.length} charges`);
  };

  const rejectPayment = (payId) => {
    const pay = pays.find(x => x.id === payId); if (!pay) return;
    const tName = tens.find(x => x.id === pay?.tid)?.name || "Tenant";
    const rejectedTxns = (pay?.txns || []).map(tx => tx.status === "pending" ? { ...tx, status: "rejected", rejectedAt: new Date().toISOString().slice(0, 10) } : tx);
    const up = { ...pay, pending: undefined, txns: rejectedTxns };
    setPays(pays.map(x => x.id === payId ? up : x));
    api.savePayment(up);
    addNotif("landlord", null, `Rejected payment from ${tName}`, "payment");
    addNotif("tenant", pay?.tid, `Your payment of \u20B9${pay?.pending?.amt?.toLocaleString()} was rejected. Please contact landlord.`, "payment");
    flash(t.payRejected, "warn");
    const rTe = tens.find(x => x.id === pay?.tid);
    if (rTe?.ph) setWaAction({ tid: pay.tid, name: rTe.name, ph: rTe.ph, msg: `\u274C *${t.payRejectedWa}*\n\nHi ${rTe.name},\n\nYour payment of \u20B9${pay?.pending?.amt?.toLocaleString()} for ${pay?.mo} could not be verified. Please contact your landlord.\n\nThank you!` });
  };

  if (receiptPay) return <div style={{ paddingTop: sp.lg }}>
    <ReceiptView
      pay={receiptPay}
      prop={(props || []).find(x => x.tid === receiptPay.tid)}
      tenant={tens.find(x => x.id === receiptPay.tid)}
      onBack={() => setReceiptPay(null)} />
  </div>;

  return <div style={{ paddingTop: sp.lg }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md }}>
      <h2 style={{ color: h.tx, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: "-0.6px" }}>{t.payments}</h2>
      <button data-coach="pay-add" onClick={() => go("addPay")} style={{ background: h.ac, border: "none", color: "#fff", padding: "9px 16px", borderRadius: 999, cursor: "pointer", fontSize: 12.5, fontWeight: 700, boxShadow: `0 6px 16px ${h.ac}40`, transition: "all 0.2s ease" }}>+ {t.markPaid}</button>
    </div>

    {/* Filter tabs */}
    <div data-coach="pay-summary" className="hide-scrollbar" style={{ display: "flex", gap: sp.xs + 2, marginBottom: sp.md, overflowX: "auto", paddingBottom: sp.xs }}>
      {[["all", t.all], ["pending", `${t.pending} (${pendingCount})`], ["paid", `${t.paid} (${paidCount})`], ["due", `${t.due} (${dueCount})`], ["advance", `Advance (${advanceCount})`]].map(([x, l]) => <button key={x} onClick={() => sT(x)} style={{
        padding: "8px 14px", borderRadius: 999, border: "none",
        background: tab === x ? `${h.ac}12` : "transparent", color: tab === x ? h.a2 : h.ts,
        cursor: "pointer", fontSize: 12.5, fontWeight: tab === x ? 700 : 500, whiteSpace: "nowrap",
        transition: "all 0.2s ease", WebkitTapHighlightColor: "transparent"
      }}>{l}</button>)}
    </div>

    {/* WhatsApp action banner */}
    {hasWa && waAction && <div className="animate-slideUp" style={{ background: `${h.ac}08`, border: `1px solid ${h.ac}30`, borderRadius: rad.md, padding: sp.md, marginBottom: sp.md, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{"\uD83D\uDCF1"} {t.sendWa} to {waAction.name}?</div>
        <div style={{ color: h.ts, fontSize: 10, lineHeight: 1.3 }}>{waAction.msg.slice(0, 60)}...</div>
      </div>
      <div style={{ display: "flex", gap: sp.xs + 2 }}>
        <button onClick={() => { window.open(`https://wa.me/${normPhone(waAction.ph)}?text=${encodeURIComponent(waAction.msg)}`, "_blank"); setWaAction(null); }} style={{ background: "#25D366", color: "#fff", border: "none", borderRadius: rad.sm, padding: `${sp.sm}px ${sp.md}px`, cursor: "pointer", fontSize: 11, fontWeight: 700, transition: "all 0.2s ease" }}>{t.sendWa}</button>
        <button onClick={() => setWaAction(null)} style={{ background: h.ib, color: h.ts, border: `1px solid ${h.bd}`, borderRadius: rad.sm, padding: `${sp.sm}px ${sp.sm + 2}px`, cursor: "pointer", fontSize: 11, transition: "all 0.2s ease" }}>{"\u2715"}</button>
      </div>
    </div>}

    {/* Stat cards \u2014 solid green Received / coral Due (reference mock) */}
    <div style={{ display: "flex", gap: 12, marginBottom: sp.lg }}>
      <div style={{ flex: 1, padding: "16px 18px", borderRadius: 20, background: "linear-gradient(135deg, #34d399 0%, #059669 100%)", color: "#fff", boxShadow: "0 10px 24px rgba(5,150,105,0.22)" }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums" }}>{"\u20B9"}{paidTotal.toLocaleString()}</div>
        <div style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.92, marginTop: 3 }}>{t.totalReceived || "Total Received"}</div>
      </div>
      <div style={{ flex: 1, padding: "16px 18px", borderRadius: 20, background: h.gl, color: "#fff", boxShadow: `0 10px 24px ${h.ac}38` }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums" }}>{"\u20B9"}{dueTotal.toLocaleString()}</div>
        <div style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.92, marginTop: 3 }}>{t.totalDue || "Total Due"}</div>
      </div>
    </div>
    {/* Advance stat \u2014 white card row with indigo amount (reference mock) */}
    {advanceTotal > 0 && <div onClick={() => sT("advance")} style={{ background: h.cd, borderRadius: 18, boxShadow: h.sh, padding: "13px 16px", marginBottom: sp.lg, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ ...icBox(h.in || h.ac, 38), flexShrink: 0 }}><span style={{ fontSize: 16 }}>{"\uD83C\uDF81"}</span></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>Advance Received</div>
        <div style={{ color: h.ts, fontSize: 11, marginTop: 1 }}>{advanceCount} tenant{advanceCount !== 1 ? "s" : ""} with advance payments</div>
      </div>
      <div style={{ color: h.in || h.ac, fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{"\u20B9"}{advanceTotal.toLocaleString()}</div>
      <span style={{ color: h.tt || h.sc, fontSize: 15, fontWeight: 700 }}>{"\u203A"}</span>
    </div>}

    {(() => {
      const renderPayCard = (p) => {
        const te = tens.find(x => x.id === p.tid);
        if (editId === p.id) return <div key={p.id} style={{ ...crd(h), padding: sp.xl }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.md }}><div style={{ color: h.ac, fontWeight: 600, fontSize: 14 }}>{t.editPay}</div><button onClick={() => sEd(null)} style={{ background: "none", border: "none", color: h.ts, cursor: "pointer", fontSize: 14 }}>{"\u2715"}</button></div>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: sp.sm + 2 }}>{te?.name} {"\u2014"} {moLabel(p.mo)}</div>
          <Fld l={t.amt} v={eAmt} set={sEA} h={h} type="number" /><Fld l={t.commonRs} v={eCom} set={sEC} h={h} type="number" />
          <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: sp.xs + 2, letterSpacing: "0.3px", textTransform: "uppercase" }}>{t.method}</label>
          <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.md }}>{["cash", "upi", "bank"].map(m => <button key={m} onClick={() => sEM(m)} style={{ flex: 1, padding: sp.sm, borderRadius: rad.sm + 2, border: `1px solid ${eMth === m ? h.ac : h.bd}`, background: eMth === m ? `${h.ac}15` : "transparent", color: eMth === m ? h.ac : h.ts, cursor: "pointer", fontSize: 12, fontWeight: eMth === m ? 600 : 400, transition: "all 0.2s ease" }}>{m === "cash" ? t.cash : m === "upi" ? t.upi : t.bank}</button>)}</div>
          <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: sp.xs + 2, letterSpacing: "0.3px", textTransform: "uppercase" }}>{t.status}</label>
          <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.lg }}>{["paid", "due"].map(s => <button key={s} onClick={() => sES(s)} style={{ flex: 1, padding: sp.sm, borderRadius: rad.sm + 2, border: `1px solid ${eSt === s ? (s === "paid" ? h.gn : h.rd) : h.bd}`, background: eSt === s ? (s === "paid" ? `${h.gn}15` : `${h.rd}15`) : "transparent", color: eSt === s ? (s === "paid" ? h.gn : h.rd) : h.ts, cursor: "pointer", fontSize: 12, fontWeight: eSt === s ? 600 : 400, transition: "all 0.2s ease" }}>{s === "paid" ? t.paid : t.due}</button>)}</div>
          <button onClick={saveEdit} style={btnP(h)}>{t.save}</button></div>;

        // Paid + non-pending rows collapse by default; everything else
        // (due, partial, pending, advance-only) stays always-expanded
        // because the landlord needs the action buttons in view.
        const isPaidCard = p.st === "paid" && !p.pending;
        const isExpanded = !isPaidCard || expandedPaidIds.has(p.id);

        // Status accent — drives the tiny tinted icon square + status pill
        // on a calm white card (reference mock). Gradients retired.
        const stC =
          p.pending           ? h.wn :   // pending — amber
          p.st === "paid"     ? h.gn :   // paid    — green
          p.st === "partial"  ? h.wn :   // partial — amber
          h.a2;                          // due/overdue — coral

        return <div
          key={p.id}
          onClick={(e) => {
            if (!isPaidCard) return;
            // Don't toggle when the click hits an interactive child —
            // Edit / Delete / Payment History toggle / etc. should
            // still work normally when the card is expanded.
            if (e.target.closest('button, a, input, textarea, select, label')) return;
            togglePaidExpanded(p.id);
          }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = h.s2; }}
          onMouseOut={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = h.sh; }}
          style={{
            ...crd(h),
            background: h.cd,
            border: "none",
            borderRadius: 20,
            boxShadow: h.sh,
            cursor: isPaidCard ? "pointer" : "default",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
        >
          {(() => {
            const gross = p.amt + (p.com || 0);
            const paidAmt = (p.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0);
            const remaining = Math.max(0, gross - paidAmt);
            const isPartial = p.st === "partial" || (paidAmt > 0 && paidAmt < gross && p.st !== "paid");
            const hasAdvance = (p.advance || 0) > 0;
            const isAdvanceOnly = gross === 0 && hasAdvance;
            const isMaintRow = (p.mo || "").startsWith("🔧");
            const displayAmt = isAdvanceOnly ? p.advance : isPartial ? remaining : gross;
            // Theme text on the white card (was white-on-gradient).
            const W = h.tx, W85 = h.ts, W70 = h.tt || h.sc;
            const stLabel = isAdvanceOnly ? "\uD83D\uDCB0 Advance" : p.st === "paid" ? (hasAdvance ? "Paid + Advance" : t.paid) : isPartial ? "Partial" : p.pending ? "\u23F3 " + t.pending : isOverdue(p.mo) ? "\uD83D\uDD34 " + t.overdue : t.due;
            return <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: sp.sm }}>
                <div style={{ ...icBox(stC, 36), flexShrink: 0 }}><span style={{ fontSize: 14 }}>{isAdvanceOnly ? "💰" : p.pending ? "⏳" : p.st === "paid" ? "✓" : "!"}</span></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: W, fontWeight: 700, fontSize: 13.5, letterSpacing: "-0.1px" }}>{isAdvanceOnly ? "\uD83D\uDCB0 Advance — " + (te?.name || `Unknown (${p.tid || 'no tid'})`) : (te?.name || `Unknown (${p.tid || 'no tid'})`)}</div>
                  {/* Paid rows show the actual payment date (like the
                      tenant card); unpaid rows show the bill month.
                      Maintenance rows keep the service name in front so
                      it isn't lost when the date takes over. */}
                  <div style={{ color: W85, fontSize: 11, marginTop: 1 }}>{p.st === "paid" && p.dt
                    ? `${isMaintRow ? moLabel(p.mo) + " \u00B7 " : ""}${t.paidOn || "Paid on"} ${p.dt}${p.mth ? " \u00B7 " + p.mth.toUpperCase() : ""}`
                    : `${moLabel(p.mo)} \u00B7 ${p.mth || "\u2014"}`}</div>
                  {/* Rent period this payment covers (start-end of the
                      cycle), derived from the month + tenant's due day. */}
                  {!isAdvanceOnly && rentPeriod(p.mo, te?.rent_due_day) && (
                    <div style={{ color: W70, fontSize: 10, marginTop: 1, display: "flex", alignItems: "center", gap: 3 }}>
                      <span>{"\uD83D\uDDD3\uFE0F"}</span>{rentPeriod(p.mo, te?.rent_due_day)} {t.rentAmt || "Rent"}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: W, fontWeight: 800, fontSize: 16, letterSpacing: "-0.2px" }}>{"\u20B9"}{displayAmt.toLocaleString()}</div>
                    <span style={{ display: "inline-flex", alignItems: "center", background: `${stC}12`, color: stC, padding: "3px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1px", lineHeight: 1.5, marginTop: 2 }}>{stLabel}</span>
                  </div>
                  {/* Expand / collapse chevron \u2014 only paid cards toggle.
                      Rotates 180\u00B0 when expanded. */}
                  {isPaidCard && <span style={{
                    color: W70, fontSize: 14, transition: "transform 0.2s",
                    display: "inline-block",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}>{"\u25BE"}</span>}
                </div>
              </div>
              {isExpanded && <>
                {p.com > 0 && <div style={{ color: W85, fontSize: 10, marginTop: sp.xs }}>{t.rentAmt} {"\u20B9"}{p.amt.toLocaleString()} + {t.commonAmt} {"\u20B9"}{p.com}</div>}
                {isPartial && paidAmt > 0 && <div style={{ ...glassPanel(h), display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", borderRadius: 8 }}>
                  <span style={{ fontSize: 12 }}>{"\u2705"}</span>
                  <span style={{ color: "#16a34a", fontSize: 11, fontWeight: 600 }}>Paid: {"\u20B9"}{paidAmt.toLocaleString()}</span>
                  <span style={{ color: h.sc, fontSize: 10, marginLeft: "auto" }}>of {"\u20B9"}{gross.toLocaleString()}</span>
                </div>}
                {hasAdvance && <div style={{ ...glassPanel(h), display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 10px", borderRadius: 8 }}>
                  <span style={{ fontSize: 12 }}>{"\uD83D\uDCB0"}</span>
                  <span style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>Advance: {"\u20B9"}{p.advance.toLocaleString()}</span>
                </div>}
              </>}
            </>;
          })()}

          {/* Pending approval area — paid cards never have pending,
              but defensively gate on isExpanded so collapsed paid
              rows stay clean even if pending leaks back in. */}
          {isExpanded && p.pending && (() => {
            // Detect sibling pending rows from the same tenant submission (TenPayNow stamps batchId).
            const batchId = p.pending.batchId;
            const batchSiblings = batchId ? pays.filter(x => x.pending?.batchId === batchId) : [];
            const isBatch = batchSiblings.length > 1;
            const batchTotal = isBatch ? (p.pending.batchTotal || batchSiblings.reduce((s, x) => s + (x.pending?.amt || 0), 0)) : 0;
            const myIndex = isBatch ? batchSiblings.findIndex(x => x.id === p.id) : -1;
            return <div style={{ ...glassPanel(h), borderRadius: rad.sm + 2, padding: sp.md, marginTop: sp.sm + 2, boxShadow: "0 2px 8px rgba(0,0,0,0.10)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.xs + 2, marginBottom: sp.sm }}>
                <div style={icBox(h.wn, 24)}><span style={{ fontSize: 11 }}>{"\u23F3"}</span></div>
                <span style={{ color: h.wn, fontWeight: 600, fontSize: 12 }}>{t.awaitApproval}</span>
                {isBatch && <span style={{ marginLeft: "auto", background: `${h.ac}15`, color: h.ac, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>🔗 {myIndex + 1} of {batchSiblings.length}</span>}
              </div>
              {isBatch && <div style={{ background: `${h.ac}06`, borderRadius: rad.sm, padding: sp.sm, marginBottom: sp.sm, border: `1px solid ${h.ac}20` }}>
                <div style={{ color: h.ac, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Part of ₹{batchTotal.toLocaleString()} batch submission</div>
                {batchSiblings.map(s => {
                  const sTotal = (s.amt || 0) + (s.com || 0);
                  const sPending = s.pending?.amt || 0;
                  const isThis = s.id === p.id;
                  return <div key={s.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, padding: "2px 0", color: isThis ? h.tx : h.ts, fontWeight: isThis ? 700 : 400 }}>
                    <span>{isThis ? "▸ " : "• "}{s.mo}{isThis ? " (this card)" : ""}</span>
                    <span>₹{sPending.toLocaleString()}{sPending < sTotal ? ` of ₹${sTotal.toLocaleString()}` : ""}</span>
                  </div>;
                })}
              </div>}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.xs }}><span style={{ color: h.ts, fontSize: 11 }}>{t.amount}</span><span style={{ color: h.tx, fontWeight: 700, fontSize: 13 }}>{"\u20B9"}{p.pending.amt.toLocaleString()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.xs }}><span style={{ color: h.ts, fontSize: 11 }}>{t.method}</span><span style={{ color: h.tx, fontSize: 11 }}>{p.pending.mth.toUpperCase()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.xs }}><span style={{ color: h.ts, fontSize: 11 }}>{t.date}</span><span style={{ color: h.tx, fontSize: 11 }}>{p.pending.dt}</span></div>
              {p.pending.amt < (p.amt + (p.com || 0)) && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: sp.xs }}><span style={{ color: h.ts, fontSize: 11 }}>{t.remaining}</span><span style={{ color: h.rd, fontSize: 11, fontWeight: 600 }}>{"\u20B9"}{((p.amt + (p.com || 0)) - p.pending.amt).toLocaleString()}</span></div>}
              {isBatch && <button onClick={() => approveBatch(batchId)} style={{ width: "100%", padding: sp.sm + 2, borderRadius: rad.sm, border: `1px solid ${h.gn}`, background: h.gn, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700, marginTop: sp.sm + 2, transition: "all 0.2s ease" }}>✓ Approve all {batchSiblings.length} (₹{batchTotal.toLocaleString()})</button>}
              <div style={{ display: "flex", gap: sp.sm, marginTop: sp.sm + 2 }}>
                <button onClick={() => approvePayment(p.id)} style={{ flex: 1, padding: sp.sm + 2, borderRadius: rad.sm, border: `1px solid ${h.gn}25`, background: `${h.gn}10`, color: h.gn, cursor: "pointer", fontSize: 12, fontWeight: 700, transition: "all 0.2s ease" }}>{"\u2713 " + t.approve}{isBatch ? " just this" : ""}</button>
                <button onClick={() => rejectPayment(p.id)} style={{ flex: 1, padding: sp.sm + 2, borderRadius: rad.sm, border: `1px solid ${h.rd}25`, background: `${h.rd}10`, color: h.rd, cursor: "pointer", fontSize: 12, fontWeight: 700, transition: "all 0.2s ease" }}>{"\u2715 " + t.reject}</button>
              </div>
            </div>;
          })()}

          {/* Action buttons — Edit / Delete / Mark Paid / etc. Hidden
              when a paid card is collapsed (the row already conveys
              "Paid · ₹amount", no further action needed at rest). */}
          {isExpanded && (() => {
            // Expose an "Apply Advance" affordance when this due record
            // belongs to a tenant who has unapplied advance credit. Covers
            // pre-existing dues that were created before auto-application
            // was wired in (and any future manual fallback).
            const availableAdvance = (p.st === "due" || p.st === "partial")
              ? pays.filter(x => x.tid === p.tid && (x.advance || 0) > 0)
                    .reduce((s, x) => s + (x.advance || 0), 0)
              : 0;
            const dueAmt = (p.amt || 0) + (p.com || 0) -
                           ((p.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0));
            const canApplyAdvance = availableAdvance > 0 && dueAmt > 0;

            const onApplyAdvance = () => {
              const { newPay: finalPay, updatedPays, consumed } = applyAdvanceToDue(
                { ...p, amt: 0, com: dueAmt, txns: p.txns || [] }, // reshape: dueAmt only
                pays,
                p.tid
              );
              if (consumed <= 0) { flash("No advance available", "error"); return; }
              // Preserve original amt/com split; only copy the updated st + new advance-applied txn
              const advTxn = finalPay.txns[finalPay.txns.length - 1];
              const merged = {
                ...p,
                st: finalPay.st,
                dt: finalPay.dt,
                txns: [...(p.txns || []), advTxn],
              };
              setPays(prev => {
                const withSourcesReduced = prev.map(x => updatedPays.find(u => u.id === x.id) || x);
                return withSourcesReduced.map(x => x.id === merged.id ? merged : x);
              });
              api.savePayment(merged);
              updatedPays.forEach(u => api.savePayment(u));
              addNotif("tenant", p.tid,
                consumed >= dueAmt
                  ? `✅ ₹${consumed.toLocaleString()} from your advance credit was applied to your ${p.mo} rent. ${p.mo} is now fully paid.`
                  : `💰 ₹${consumed.toLocaleString()} from your advance credit was applied to your ${p.mo} rent. ₹${(dueAmt - consumed).toLocaleString()} is still due for ${p.mo}.`,
                "payment");
              flash(consumed >= dueAmt
                ? `Applied ₹${consumed.toLocaleString()} advance to ${p.mo} — fully paid ✓`
                : `Applied ₹${consumed.toLocaleString()} advance to ${p.mo} — ₹${(dueAmt - consumed).toLocaleString()} still due`);
            };

            // Soft tinted action chips on the white card (reference mock).
            const gbtn = (c, fw = 600) => ({ flex: 1, padding: "9px 10px", borderRadius: 12, border: "none", background: `${c}0f`, color: c, cursor: "pointer", fontSize: 12, fontWeight: fw, transition: "all 0.2s ease", whiteSpace: "nowrap" });
            return !p.pending && <div style={{ display: "flex", gap: sp.xs + 2, marginTop: sp.sm + 2, flexWrap: "wrap" }}>
            {p.st === "paid" && <button onClick={() => setReceiptPay(p)} style={gbtn(h.ac)}>{"🧾 " + (t.receipt || "Receipt")}</button>}
            <button onClick={() => startEdit(p)} style={gbtn(h.ac)}>{"\u270F\uFE0F " + t.edit}</button>
            {canApplyAdvance && <button onClick={onApplyAdvance} style={gbtn(h.ac, 700)}>💰 Use Advance (₹{Math.min(availableAdvance, dueAmt).toLocaleString()})</button>}
            {(p.st === "due" || p.st === "partial") && <><button onClick={() => setMethodPickerFor(p.id)} style={gbtn(h.gn)}>{t.markPaid}</button>
              {hasWa && <button onClick={() => {
                addNotif("tenant", p.tid, `Reminder: Rent of \u20B9${p.amt.toLocaleString()} is due. Please pay soon.`, "alert");
                if (te?.ph) {
                  const totalDue = p.amt + (p.com || 0);
                  const overdue = isOverdue(p.mo);
                  const text = encodeURIComponent(`${overdue ? "\uD83D\uDD34 *RENT OVERDUE*" : "\u26A0\uFE0F *Rent Reminder*"} \u26A0\uFE0F\n\nHi ${te.name},\n\n${overdue ? "Your rent for" : "This is a gentle reminder that your rent for"} *${moLabel(p.mo)}* is ${overdue ? "overdue" : "currently due"}.\n\n*Amount Due:* \u20B9${totalDue.toLocaleString()}` + (p.com > 0 ? `\n(Rent: \u20B9${p.amt.toLocaleString()} + Common Charges: \u20B9${p.com})` : "") + `\n\n\uD83D\uDC49 *Pay Here:* ${window.location.origin}\n\nPlease click the link to open the app, log in with your phone number, and make your payment.${overdue ? " This is urgent \u2014 please pay immediately." : " Contact us if you've already paid."}\n\nThank you!`);
                  window.open(`https://wa.me/${normPhone(te.ph)}?text=${text}`, "_blank");
                } else {
                  flash(t.noPhone, "error");
                }
              }} style={gbtn(h.wn)}>{t.whatsappRemind}</button>}</>}
            <button onClick={() => deletePayment(p.id)} style={gbtn(h.rd)}>{"\u2715 " + t.delete}</button>
          </div>;
          })()}

          {/* Inline method picker — shown after Mark Paid is clicked.
              Replaces the old hard-coded "razorpay/cash" with the actual
              payment mode the landlord received. */}
          {isExpanded && methodPickerFor === p.id && (
            <div style={{ ...glassPanel(h), marginTop: sp.sm, padding: sp.md, borderRadius: rad.sm, boxShadow: "0 2px 8px rgba(0,0,0,0.10)" }}>
              <div style={{ fontSize: 11, color: h.tx, fontWeight: 600, marginBottom: sp.sm, letterSpacing: 0.2 }}>
                How was this payment received?
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {RENT_PAYMENT_METHODS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => markPaidWithMethod(p, m.id)}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      border: `1px solid ${h.gn}40`, background: h.cd,
                      color: h.gn, cursor: "pointer", fontSize: 12, fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <span>{m.icon}</span><span>{m.label}</span>
                  </button>
                ))}
                <button
                  onClick={() => setMethodPickerFor(null)}
                  style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${h.bd}`, background: h.cd, color: h.sc, cursor: "pointer", fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>;
      };

      // Merge consecutive "🔧"-prefixed maintenance rows for the same tenant
      // into a single collapsible "🔧 Common & Maint" parent card. This keeps
      // the landlord's view from being cluttered when one tenant has multiple
      // small maintenance charges (motor + L&M + water leak + ...).
      const groupMaintRows = (rows) => {
        const out = [];
        const seen = {};
        for (const r of rows) {
          if ((r.mo || "").startsWith("🔧")) {
            if (!seen[r.tid]) {
              seen[r.tid] = { isCombinedMaint: true, tid: r.tid, id: `combined_maint_${r.tid}`, subRows: [] };
              out.push(seen[r.tid]);
            }
            seen[r.tid].subRows.push(r);
          } else {
            out.push(r);
          }
        }
        return out;
      };

      const renderCombinedMaintCard = (item) => {
        const { tid, subRows } = item;
        const te = tens.find(x => x.id === tid);
        const total = subRows.reduce((s, r) => s + (r.amt || 0) + (r.com || 0), 0);
        const allPaid = subRows.every(r => r.st === "paid" && !r.pending);
        const anyPending = subRows.some(r => r.pending);
        const isOpen = !!expMaint[tid];
        const statusLabel = allPaid ? "✓ Paid" : anyPending ? "⏳ Pending" : "Mixed";
        const statusColor = allPaid ? h.gn : anyPending ? h.wn : h.ts;
        // If only one row, fall back to the regular renderer — no point in a "1-charge" wrapper.
        if (subRows.length === 1) return renderPayCard(subRows[0]);
        return <div key={item.id} style={{ ...crd(h), borderLeft: anyPending ? `3px solid ${h.wn}` : undefined }}>
          <div onClick={() => setExpMaint(e => ({ ...e, [tid]: !e[tid] }))} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div>
              <div style={{ color: te?.name ? h.tx : h.wn, fontWeight: 600, fontSize: 13, letterSpacing: "-0.1px" }}>{te?.name || `Unknown (${tid || 'no tid'})`}</div>
              <div style={{ color: h.ts, fontSize: 11, marginTop: 1 }}>🔧 Common & Maint · {subRows.length} charges</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: sp.sm, textAlign: "right" }}>
              <div>
                <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, letterSpacing: "-0.2px" }}>{"\u20B9"}{total.toLocaleString()}</div>
                <span style={bdg(`${statusColor}15`, statusColor)}>{statusLabel}</span>
              </div>
              <span style={{ color: h.ts, fontSize: 14, transition: "transform 0.2s", display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>{"\u25BE"}</span>
            </div>
          </div>
          {isOpen && <div style={{ marginTop: sp.sm + 2, paddingTop: sp.sm + 2, borderTop: `1px dashed ${h.bd}` }}>
            {subRows.map(r => renderPayCard(r))}
          </div>}
        </div>;
      };

      const renderItem = (item) => item.isCombinedMaint ? renderCombinedMaintCard(item) : renderPayCard(item);

      if (tab === "paid" || tab === "due") {
        const grouped = {};
        f.forEach(p => {
          const te = tens.find(x => x.id === p.tid);
          const name = te?.name || "Unknown";
          if (!grouped[name]) grouped[name] = { te, pays: [], total: 0 };
          grouped[name].pays.push(p);
          grouped[name].total += p.amt + (p.com || 0);
        });
        return Object.entries(grouped).map(([name, g]) => {
          const isOpen = !!expTen[name];
          return <div key={name} style={{ marginBottom: sp.md }}>
            <div onClick={() => setExpTen(e => ({ ...e, [name]: !e[name] }))} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${sp.md}px ${sp.lg - 2}px`, borderRadius: isOpen ? `${rad.sm + 2}px ${rad.sm + 2}px 0 0` : rad.sm + 2, background: tab === "paid" ? `${h.gn}08` : `${h.rd}08`, cursor: "pointer", border: `1px solid ${tab === "paid" ? `${h.gn}20` : `${h.rd}20`}`, borderBottom: isOpen ? `1px dashed ${tab === "paid" ? `${h.gn}25` : `${h.rd}25`}` : undefined, transition: "all 0.2s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm + 2 }}>
                <Avatar src={g.te?.avatar_url} name={name} size={32} bg={tab === "paid" ? `${h.gn}18` : `${h.rd}18`} color={tab === "paid" ? h.gn : h.rd} fontSize={13} />
                <div>
                  <div style={{ color: h.tx, fontWeight: 600, fontSize: 13, letterSpacing: "-0.1px" }}>{name}</div>
                  <div style={{ color: h.ts, fontSize: 11 }}>{g.pays.length} {g.pays.length === 1 ? "record" : "records"}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
                <div style={{ color: tab === "paid" ? h.gn : h.rd, fontWeight: 700, fontSize: 14, letterSpacing: "-0.2px" }}>{"\u20B9"}{g.total.toLocaleString()}</div>
                <span style={{ color: h.ts, fontSize: 16, transition: "transform 0.2s", display: "inline-block", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>{"\u25BE"}</span>
              </div>
            </div>
            {isOpen && <div style={{ borderRadius: `0 0 ${rad.sm + 2}px ${rad.sm + 2}px`, background: h.bg, border: `1px solid ${tab === "paid" ? `${h.gn}20` : `${h.rd}20`}`, borderTop: "none", padding: sp.sm }}>
              {groupMaintRows(g.pays).map(item => renderItem(item))}
            </div>}
          </div>;
        });
      }
      // Flat tabs (all / pending / advance): on desktop lay the cards out
      // in a 2-column grid so the wide canvas isn't one tall stack. Each
      // card is unchanged (all money flows intact); alignItems:start lets
      // expandable cards keep their natural height. Mobile = single column.
      const flatItems = groupMaintRows(f).map(item => renderItem(item));
      return IS_DESKTOP
        ? <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.md, alignItems: "start" }}>{flatItems}</div>
        : flatItems;
    })()}
  </div>;
}

export default PayPage;
